import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AccessComponentEnum,
  AccessLevelEnum,
  DocumentationCategory,
} from 'graphql-common';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import Table from '@lib/components/Table/Table';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import PageHeader from '@lib/layouts/DashboardLayout/PageHeader/PageHeader';
import Search from '@lib/components/Search/Search';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import ConfirmationModal from '@lib/components/ConfirmationModal/ConfirmationModal';
import ListEmptyState from '@lib/components/ListEmptyState/ListEmptyState';
import useSearchParam from '@lib/hooks/useSearchParam';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import Trans from '@lib/components/Trans/Trans';
import ViewOnlyChip from '@lib/components/ViewOnlyChip/ViewOnlyChip';
import getStatusFromError from '@lib/utils/getStatusFromError';
import getDescriptionFromError from '@lib/utils/getDescriptionFromError';
import Modal from '@lib/components/Modal/Modal';
import { IconName } from '@lib/components/Modal/enums';
import usePrevious from '@lib/hooks/usePrevious';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import useActions from './utils/useActions';
import useModalManagement from './utils/useModalManagement';
import getDefaultValuesFromQueryData from './utils/getDefaultValuesFromQueryData';
import { getActions, getColumns } from './utils/tableHelpers';
import DocumentCategoryForm from './DocumentCategoryForm';

export default function DocumentCategoriesContent() {
  const { t } = useTranslation();
  const [searchQuery] = useSearchParam();
  const [selectedCategory, setSelectedCategory] =
    useState<DocumentationCategory | null>(null);

  const { getUserAccessByComponent }: ContextProps = useOutletContext();
  const isViewOnly =
    getUserAccessByComponent(
      AccessComponentEnum.DocumentationCategoriesManagement,
    ) === AccessLevelEnum.Read;

  const {
    closeCategoryModal,
    closeDeleteModal,
    isCategoryModalOpened,
    isDeleteModalOpened,
    openCategoryModal,
    openDeleteModal,
  } = useModalManagement({ setSelectedCategory });

  const isCategoryModalOpenedPrev = usePrevious(isCategoryModalOpened);

  const {
    categoryMutationReset,
    categoryQuery,
    onConfirmedDelete,
    onDeleteHandler,
    onEditHandler,
    onSubmitCategoryForm,
    categoryMutationError,
    categoryMutationLoading,
    useEdmDocumentationCategoriesQueryHookResult,
  } = useActions({
    closeCategoryModal,
    closeDeleteModal,
    isPlanLocked: false,
    openCategoryModal,
    openDeleteModal,
    selectedCategory,
    setSelectedCategory,
  });

  useEffect(() => {
    if (isCategoryModalOpenedPrev && !isCategoryModalOpened) {
      categoryMutationReset();
    }
  }, [isCategoryModalOpened, categoryMutationReset, isCategoryModalOpenedPrev]);

  const {
    collection: categories,
    error: categoriesError,
    firstLoading: categoriesFirstLoading,
    loading: categoriesLoading,
    totalCount: totalCategoriesCount,
  } = useEdmDocumentationCategoriesQueryHookResult;

  const categoryData = categoryQuery.data?.data as DocumentationCategory | null;
  const categoryQueryError = categoryQuery.error;
  const categoryQueryLoading = categoryQuery.loading;

  const tableColumns = getColumns({ t, onEditHandler, isViewOnly });
  const tableActions = getActions({
    t,
    onEditHandler,
    onDelete: onDeleteHandler,
    isViewOnly,
  });

  if (categoriesError) {
    return (
      <ErrorPage
        status={getStatusFromError(categoriesError)}
        description={getDescriptionFromError(categoriesError)}
      />
    );
  }

  const emptyState = (
    <ListEmptyState
      title={t('no-categories-yet')}
      text={t('no-categories-text')}
      actions={
        isViewOnly
          ? []
          : [
              <Button
                key="add"
                buttonText={t('create-new-category')}
                buttonType={ButtonTypes.primaryFilled}
                leftIcon="add"
                onClick={openCategoryModal}
              />,
            ]
      }
    />
  );

  return (
    <>
      <PageHeader title={t('documentation-categories')}>
        {isViewOnly ? (
          <ViewOnlyChip />
        ) : (
          <Button
            buttonText={t('create-new-category')}
            leftIcon="add"
            onClick={openCategoryModal}
          />
        )}
      </PageHeader>
      <PageBody>
        {categoriesFirstLoading ? (
          <CircleLoader />
        ) : (
          <>
            {(!!categories?.length || searchQuery) && (
              <Search
                placeholder={t('documentation-categories-search-placeholder')}
              />
            )}
            <Table
              actions={tableActions}
              columns={tableColumns}
              counterText={t('category-plural', {
                count: totalCategoriesCount,
              })}
              data={categories}
              emptyState={emptyState}
              id="documentation-categories"
              loading={categoriesLoading}
              totalEntries={totalCategoriesCount}
            />
          </>
        )}
        <Modal
          isOpen={isCategoryModalOpened}
          title={
            selectedCategory
              ? t('edit-documentation-category')
              : t('create-new-category')
          }
          onClose={closeCategoryModal}
        >
          <DocumentCategoryForm
            defaultValues={
              selectedCategory && categoryData
                ? getDefaultValuesFromQueryData(categoryData)
                : undefined
            }
            graphQLErrors={
              (categoryQueryError || categoryMutationError)?.graphQLErrors
            }
            loading={categoryQueryLoading || categoryMutationLoading}
            onCancel={closeCategoryModal}
            onSubmit={onSubmitCategoryForm}
          />
        </Modal>
        <ConfirmationModal
          isOpen={isDeleteModalOpened}
          onClose={closeDeleteModal}
          onConfirm={onConfirmedDelete}
          title={t('are-you-sure')}
          description={
            <Trans
              i18nKey="delete-category-text"
              values={{ name: selectedCategory?.name }}
            />
          }
          confirmButtonLabel={t('yes-delete')}
          icon={IconName.Delete}
          cancelButtonLabel={t('cancel')}
          useProgress
        />
      </PageBody>
    </>
  );
}
